import { SvgIconTypeMap } from '@material-ui/core'
import { OverridableComponent } from '@material-ui/core/OverridableComponent'
import { CheckCircle } from '@material-ui/icons'
import { CustomTooltip } from 'components/facets'
import { useConfirm } from 'lib/hooks'
import { useTranslation } from 'react-i18next'
import './entity-box.css'

export interface EntityBoxProps {
  children?: React.ReactNode
  contentRow: React.ReactNode
  titleRow?: React.ReactNode | null
  onDelete?: () => void
  onClone?: () => void
  onConvertTo?: () => void
  convertToLabel?: string
  viewOnly?: boolean
  disabled?: boolean
  Icon?: OverridableComponent<SvgIconTypeMap<unknown, 'svg'>> | null
  dataTestid?: string
  competitorOrStrategy?: boolean
  bulkSettings?: boolean
  hideTitleUnderline?: boolean
  selected?: boolean
}

export const EntityBox = ({
  onDelete,
  onConvertTo,
  contentRow,
  titleRow,
  Icon,
  viewOnly = false,
  children,
  dataTestid,
  disabled = false,
  competitorOrStrategy = false,
  bulkSettings = false,
  onClone,
  convertToLabel,
  hideTitleUnderline = false,
  selected = false,
}: EntityBoxProps): JSX.Element => {
  const { t } = useTranslation()
  const [confirmDelete, ConfirmDeleteDialog] = useConfirm(
    t('common.confirmDelete'),
    '',
    () => onDelete && onDelete()
  )

  const variantClass = competitorOrStrategy
    ? 'entity-box--card'
    : bulkSettings
    ? 'entity-box--full'
    : ''

  return (
    <div className={`entity-box ${variantClass} ${disabled ? 'entity-box--disabled' : ''}`}>
      <div className="entity-box__card">
        <div className="entity-box__body">
          <div className="entity-box__header">
            <div className="entity-box__title">
              {titleRow && (
                <div
                  className={`entity-box__title-row ${
                    hideTitleUnderline ? 'entity-box__title-row--no-underline' : ''
                  }`}
                >
                  {titleRow}
                </div>
              )}
            </div>
            <div className="entity-box__actions">
              {Icon && <Icon className="entity-box__type-icon" />}
              {!viewOnly && onClone && (
                <CustomTooltip title={t('common.duplicate') as string}>
                  <div onClick={onClone}>
                    <i className="fa-light fa-copy table-action-icon"></i>
                  </div>
                </CustomTooltip>
              )}
              {!viewOnly && onConvertTo && (
                <CustomTooltip title={convertToLabel ?? 'Convert to'}>
                  <div onClick={onConvertTo} data-testid="convert-to">
                    <i className="fa-light fa-arrows-repeat table-action-icon"></i>
                  </div>
                </CustomTooltip>
              )}
              {!viewOnly && onDelete && (
                <CustomTooltip title={t('common.delete') as string}>
                  <div onClick={confirmDelete} data-testid={dataTestid}>
                    <i className="fa-light fa-trash table-action-icon"></i>
                  </div>
                </CustomTooltip>
              )}
            </div>
          </div>
          <div className="entity-box__content">{contentRow}</div>
          {competitorOrStrategy && selected && <CheckCircle className="entity-box__check" />}
        </div>
        {children}
      </div>
      <ConfirmDeleteDialog />
    </div>
  )
}
